import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Colors from "./Colors";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const WarehouseList = ({ users, active }) => {
  const [productData, setProductData] = useState([]);
  const navigate = useNavigate();
  const redirect = data => {
    navigate("/product-details", { state: { productId: data.productId } });
  };

  // Filter users based on `active` status
  const showData = users.filter(e =>
    active === 0
      ? e
      : active === 1
      ? e.accountStatus === "active"
      : active === 2
      ? e.accountStatus === "pending"
      : active === 3
      ? e.accountStatus === "restricted"
      : e.accountStatus === "picked-up"
  );

  useEffect(() => {
    // Initialize color selection (set first color as selected)
    const updatedUsers = showData.map(product => ({
      ...product,
      colors:
        product.colors?.map((color, index) => ({
          ...color,
          isColor: index === 0, // Set first color as selected initially
        })) || [],
    }));
    setProductData(updatedUsers);
  }, [users]);

  // Handle color selection for a specific row
  const handleColorChange = (productId, selectedColorCode) => {
    setProductData(prevProducts =>
      prevProducts.map(product =>
        product.productId === productId
          ? {
              ...product,
              colors: product.colors.map(color => ({
                ...color,
                isColor: color.colorCode === selectedColorCode, // Only selected color is true
              })),
            }
          : product
      )
    );
  };

  const calculateTotalInventory = colors => {
    return colors?.reduce((sum, color) => sum + (color.inventory || 0), 0);
  };

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 700 }} aria-label='customized table'>
        <TableBody>
          <TableRow>
            <StyledTableCell style={{ fontWeight: "500" }}>Name</StyledTableCell>
            <StyledTableCell style={{ fontWeight: "500" }}>Product Code</StyledTableCell>
            <StyledTableCell style={{ fontWeight: "500" }}>Colors</StyledTableCell>
            <StyledTableCell style={{ fontWeight: "500" }}>Units</StyledTableCell>
            <StyledTableCell style={{ fontWeight: "500" }}>Total Units</StyledTableCell>
            <StyledTableCell style={{ fontWeight: "500" }}>View</StyledTableCell>
          </TableRow>
        </TableBody>
        <TableBody>
          {showData.length === 0 && (
            <TableRow>
              <TableCell>No Product found</TableCell>
            </TableRow>
          )}
          {productData.map(row => {
            const totalInventory = calculateTotalInventory(row.colors);
            const selectedColor = row.colors?.find(color => color.isColor);
            return (
              <StyledTableRow key={row.productId}>
                <StyledTableCell component='th' scope='row'>
                  <span style={{ whiteSpace: "nowrap" }}>{row.nameEn}</span>
                </StyledTableCell>
                <StyledTableCell>{row.productCode || "---"}</StyledTableCell>
                <StyledTableCell>
                  {row.colors?.length > 0 && (
                    <Colors
                      product={row}
                      handleColorChange={colorCode => handleColorChange(row.productId, colorCode)}
                    />
                  )}
                  {row.colors?.length == 0 && "---"}
                </StyledTableCell>
                <StyledTableCell>{selectedColor ? selectedColor.inventory : "---"}</StyledTableCell>
                <StyledTableCell style={{ fontWeight: "600" }}>
                  {totalInventory ? totalInventory : "---"}
                </StyledTableCell>
                <StyledTableCell align='right'>
                  <span
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Tooltip
                      title='View'
                      style={{ cursor: "pointer" }}
                      onClick={() => redirect(row)}
                    >
                      <RemoveRedEyeRoundedIcon />
                    </Tooltip>
                  </span>
                </StyledTableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default WarehouseList;
